import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { getKey, isValidArray } from '../../helpers';
import { AppBanner } from '../../../components/AppBanner';
import { Hero } from '../../../components/Hero';
import { SecondaryBanner } from '../../../components/SecondaryBanner';
import Steps from '../../components/compound/Steps/StepsProvider';
import DiagonalBackground from '../../components/base/DiagonalBackground';
import Promotions from '../../components/compound/Promotions';
import RawHtml from '../../components/base/RawHtml/RawHtmlProvider';
import { RequiredDocumentation } from '../../../components/RequiredDocumentation';
import Faq from '../../components/compound/Faq/FaqProvider';
import ImageOpening from '../../components/base/ImageOpening';
import ContactForm from '../../components/compound/ContactForm';
import Chart from '../../components/compound/Chart/ChartProvider';

import PromotionsProvider from '../../components/compound/Promotions/PromotionsProvider';
import FeatureBlogProvider from '../../components/compound/FeaturedBlog/FeaturedBlogProvider';

import mapWidgetProps from './mapWidgetProps';
import CommentsForm from '../../components/compound/CommentsForm';
import Experience from '../../components/compound/Experience/ExperienceProvider';
import ExperienceArticle from '../../components/compound/ExperienceArticle';

import TicketsProvider from '../../components/compound/TicketsWhite/TicketsProvider';

import Featuredblogs from '../../components/compound/FeaturedBlog';
import { ParkingPrice } from '../../../components/ParkingPrice';
import { ShuttlePrice } from '../../../components/ShuttlePrice';
import { WheelsPrice } from '../../../components/WheelsPrice';
import { TicketCards } from '../../../features/Tickets/components/ticket-card';
import { ContentCard } from '../../../components/content-card';
import { Cards } from '../../../components/Cards';
import { TitleDescription } from '../../../components/TitleDescription';
import { TravelClubPrice } from '../../../features/travel-club';

/**
 * WidgetFactory class
 */
class WidgetFactory extends PureComponent {
  static propTypes = {
    data: PropTypes.object,
    locale: PropTypes.string,
    page_uri: PropTypes.string,
  };

  /**
   * get widget component
   * @param {string} widget - widgets
   * @returns {boolean}
   */
  static getWidgetComponent(widget) {
    switch (widget) {
      case 'hero':
        return Hero;
      case 'secondaryBanner':
        return SecondaryBanner;
      case 'step':
        return Steps;
      case 'diagonalbackground':
        return DiagonalBackground;
      case 'card':
        return Cards;
      case 'ticketcard':
        return TicketCards;
      case 'titledescription':
        return TitleDescription;
      case 'promotion':
        return PromotionsProvider;
      case 'rawhtml':
        return RawHtml;
      case 'requiredDocumentation':
        return RequiredDocumentation;
      case 'contentcard':
        return ContentCard;
      case 'faq':
        return Faq;
      case 'imageopening':
        return ImageOpening;
      case 'jobscontactform':
        return ContactForm;
      case 'chart':
        return Chart;
      case 'commentsform':
        return CommentsForm;
      case 'experience':
        return Experience;
      case 'experiencearticle':
        return ExperienceArticle;
      case 'parking':
        // NOTE: This component is deprecated and interferes with the new 
        // app's parking component. It should be removed from Strapi, until
        // then, it should be commented out.
        // return TicketsProvider;
        return null;

      case 'parkingPrice':
        return ParkingPrice;
      case 'featuredblog':
        return FeatureBlogProvider;
      case 'appBanner':
        return AppBanner;
      case 'shuttlePrice':
        return ShuttlePrice;
      case 'travelclubPrice':
        return TravelClubPrice;
      case 'wheelsPrice':
        return WheelsPrice;
      default:
        return null;
    }
  }

  /**
   * constructor class
   * @param {*} props - component props
   */
  constructor(props) {
    super(props);
    const hasWidgets = getKey(props, 'data.widgets.edges');
    const widgetList = hasWidgets && isValidArray(hasWidgets) && getKey(hasWidgets[0], 'node');
    let widgets;
    if (widgetList) {
      widgets = Object.keys(widgetList)
        .filter(widget => isValidArray(widgetList[widget]))
        .reduce((acc, widgetName) => {
          widgetList[widgetName].forEach((widgetProps) => {
            acc.push({
              component: this.constructor.getWidgetComponent(
                widgetName
              ),
              props: mapWidgetProps(widgetName, widgetProps, props.locale, props.page_uri),
              position: getKey(widgetProps, 'widgetPosition', 0)
            });
          });
          return acc;
        }, [])
        .sort((a, b) => {
          return a.position - b.position;
        });
    }

    this.state = {
      widgets
    };
  }

  /**
   * Render method
   * @returns {?JSX}
   */
  render() {
    const {
      state: { widgets }
    } = this;

    if (widgets) {
      return widgets.map(
        (widget, index) => getKey(widget, 'component') && (<widget.component {...widget.props} key={`widgetsIndex${index.toString()}`} />)
      );
    }

    return 'no hay widgets';
  }
}

export default WidgetFactory;

export const query = graphql`
  fragment Widgets on StrapiWidgetrels {
    ...AppBanners
    ...Hero
    ...Steps
    ...DiagonalBackground
    ...Cards
    ...TicketCards
    ...TitleDescription
    ...Promotions
    ...RawHtmls
    ...RequiredDocumentation
    ...ContentCards
    ...Faqs
    ...ImageOpening
    ...JobsContactForm
    ...Chart
    ...CommentsForm
    ...Experience
    ...ExperienceArticle
    ...Parking
    ...ParkingPrice
    ...SecondaryBanners
    ...ShuttlePrice
    ...TravelClubPrice
    ...Featuredblogs
    ...WheelsPrice
  }
`;
